import { Box, Portal, useDisclosure } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { spacing } from 'theme/tokens';
import { useAppSettings } from '../../contexts/AppSettingsContext';
import CommandPalette from 'components/command-palette/CommandPalette';
import Footer from 'components/footer/FooterAdmin';
import Navbar from 'components/navbar/Navbar';
import Sidebar from 'components/sidebar/Sidebar';
import { SidebarContext } from 'contexts/SidebarContext';
import { lazy, Suspense, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { accessibleAdminRoutes, landingPathForRole } from 'routes/access';
import { RootPath } from 'routes/types';
import { useAuth } from '../../features/auth/AuthProvider';
import { useUserRole } from '../../features/auth/roles';
import { useMyOrganizationsResolved } from '../../features/organization/hooks/useGetMyOrganizations';
import { useVisibleRoutes } from '../../hooks/useVisibleRoutes';
import CoachOnboardingTour from '../../features/walkthrough/components/CoachOnboardingTour';
import { useShowsOnboardingGuides } from '../../features/walkthrough/onboarding-guides-audience';

// Opens the onboarding video and guided tour when an Accueil card asks for them.
// In every environment since 2026-11-03; mounted only where the cards show
// (`useShowsOnboardingGuides`). Lazy, so excluded clubs never load its chunk.
const LabPreviewLauncher = process.env.NODE_ENV !== 'test'
  ? lazy(() => import('../../features/onboarding-lab/LabPreviewLauncher'))
  : null;

export default function AdminLayout(props: { [x: string]: any }) {
  const { ...rest } = props;
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const showGuides = useShowsOnboardingGuides();
  const getRoute = () => {
    return window.location.pathname !== '/admin/full-screen-maps';
  };
  const getActiveRoute = (routes: RoutesType[]): string => {
    let activeRoute = 'Default Brand Text';
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].name;
      }
    }
    return activeRoute;
  };
  const getActiveNavbar = (routes: RoutesType[]): boolean => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].secondary;
      }
    }
    return activeNavbar;
  };
  const getActiveNavbarText = (routes: RoutesType[]): string | boolean => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].name;
      }
    }
    return activeNavbar;
  };
  const getRoutes = (routes: RoutesType[]): any => {
    return routes.map((route: RoutesType, key: any) => {
      if (route.layout === '/admin') {
        return (
          <Route path={`${route.path}`} element={route.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };
  document.documentElement.dir = 'ltr';
  const location = useLocation();
  const { currentUser } = useAuth();
  const { onOpen } = useDisclosure();
  const { shouldAnimate, durationMultiplier } = useAppSettings();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const role = useUserRole();
  // `useUserRole()` reads the SELECTED org's `myRole`, and until
  // `my-organizations` answers there is no selected org — so it falls back to
  // the global role claim, which fails OPEN to ADMIN. On a hard reload straight
  // onto an /admin deep link that window is not merely a cosmetic flash: with
  // role=ADMIN, `accessibleAdminRoutes` mounts EVERY route, so a coach reloading
  // `/admin/users` actually renders the users page and fires its admin queries
  // (which the API 403s) before the catch-all bounces them. Withhold the
  // role-dependent chrome until the server has answered.
  //
  // Tradeoff, deliberately accepted: a failing `my-organizations` shows a blank
  // page until React Query exhausts its retries. An error counts as RESOLVED
  // (see `useMyOrganizationsResolved`), so this can never be a permanent blank —
  // and an API that cannot answer this call yields an unusable dashboard either
  // way. This is not a security boundary; the API is.
  const organizationsResolved = useMyOrganizationsResolved(Boolean(currentUser));

  // Only the routes this role may render. A coach hitting `/admin/users`
  // directly finds no matching <Route> and falls through to the catch-all
  // below, which sends them back to their own landing page.
  const routes = useMemo(() => accessibleAdminRoutes(role), [role]);
  const landingPath = landingPathForRole(role);

  const targetRoutes = useVisibleRoutes();

  if (!currentUser)
    return <Navigate to={RootPath.AUTH} replace />

  if (!organizationsResolved) return null;

  return (
    <Box>
      <SidebarContext.Provider
        value={{
          toggleSidebar,
          setToggleSidebar,
        }}
      >
        <Sidebar routes={targetRoutes} display="none" enableFavorites {...rest} />
        <Box
          float="right"
          minHeight="100vh"
          height="100%"
          overflow="auto"
          position="relative"
          maxHeight="100%"
          w={{ base: '100%', xl: 'calc( 100% - 300px )' }}
          maxWidth={{ base: '100%', xl: 'calc( 100% - 300px )' }}
          transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
          transitionDuration=".2s, .2s, .35s"
          transitionProperty="top, bottom, width"
          transitionTimingFunction="linear, linear, ease"
          paddingTop={20}
        >
          <Portal>
            <Box>
              <Navbar
                onOpen={onOpen}
                logoText={'MySportSet'}
                brandText={getActiveRoute(routes)}
                secondary={getActiveNavbar(routes)}
                message={getActiveNavbarText(routes)}
                fixed={fixed}
                layout={"/admin"}
                enableFavorites
                onCommandPaletteOpen={() => setCommandPaletteOpen(true)}
                visibleRoutes={targetRoutes}
                {...rest}
              />
            </Box>
          </Portal>

          {getRoute() ? (
            <Box
              mx="auto"
              p={spacing.page}
              pe={spacing.page.base}
              minH="calc(100vh - 80px)"
              pt="50px"
            >
              {shouldAnimate ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={location.pathname}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 * durationMultiplier, ease: 'easeOut' }}
                  >
                    <Routes location={location}>
                      {getRoutes(routes)}
                      <Route
                        path="/"
                        element={<Navigate to={landingPath} replace />}
                      />
                      <Route path="*" element={<Navigate to={landingPath} replace />} />
                    </Routes>
                  </motion.div>
                </AnimatePresence>
              ) : (
                <Routes location={location}>
                  {getRoutes(routes)}
                  <Route
                    path="/"
                    element={<Navigate to={landingPath} replace />}
                  />
                  <Route path="*" element={<Navigate to={landingPath} replace />} />
                </Routes>
              )}
            </Box>
          ) : null}
          <Box>
            <Footer />
          </Box>
        </Box>
      </SidebarContext.Provider>
      {LabPreviewLauncher && showGuides && (
        <Suspense fallback={null}>
          <LabPreviewLauncher />
        </Suspense>
      )}
      <CommandPalette
        open={commandPaletteOpen}
        setOpen={setCommandPaletteOpen}
        visibleRoutes={targetRoutes}
      />
      {/* MYS-132 — coach first-login tutorial. Layout-mounted (not per-page)
          because it navigates across routes; renders nothing for admins. Safe
          to mount here: organizationsResolved has already answered above, so
          the role it reads is the server-resolved one, not the ADMIN
          fail-open default. */}
      <CoachOnboardingTour />
    </Box>
  );
}
